import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Header from "./Header";
import Toolbar from "./toolbar/Toolbar";
import ViewerComp from "./ViewerComp";
import LibraryPopup from "../../components/modals/LibraryPopup";
import IconsPopup from "../../components/modals/IconsPopup";
import { useNavigate, useParams } from "react-router-dom";
import { defaultScene } from "./dummy";
import apiclient from "../../apiclient";
import { UnoStoreHooks } from "@simulanisindia/ui";
import {
  HOTSPOT_CREATE,
  INVENTORY_THUMBNAIL_GENERATE,
  PROJECT_DETAILS,
  PROJECT_INVENTORY,
  SCENE_DETAILS,
  SCENE_DETAILS_UPDATE,
} from "../../apiclient/apiEndpoints";
import {
  resetInventoryList,
  saveDefaultInventoryList,
  saveInventoryList,
  saveSceneList,
  resetSceneList,
  triggerFetchScene,
} from "../../store/slice/projectSlice";
import { useDebouncedAsyncEffect } from "../../hooks/useDebouncedAsyncEffect";
import { animateToHotspot } from "../../utils/threeUtils";
import { getImageDimensions } from "../../utils/general";
import { UiComponents } from "@simulanisindia/ui";
import { BackArrowIcon } from "../../assets/icons/icons";
import { DEFAULT_HOTSPOT_ACTION } from "../../utils/constant";
import PublishLoad from "./PublishLoad";
import ChatBot from "./ChatBot";
const { useUnoSelector } = UnoStoreHooks;
const { BackButton, FullScreenLoader } = UiComponents;

const EditorMain = ({
  isMobileView,
  showHeaderProps = true,
  showToolbarProps = true,
  updateSceneProps = true,
  showBackButton = true,
}) => {
  const { uuid } = useParams(); 
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const configData = useUnoSelector((state) => state.config);
  const userData = useUnoSelector((state) => state.user);
  const { sceneList } = useSelector((state) => state.projectSlice);
  const [scene, setScene] = useState(defaultScene);
  const [currentHotspot, setCurrentHotspot] = useState({});
  const [showLibrary, setShowLibrary] = useState(false);
  const [editorMode, setEditorMode] = useState(true);
  const [editPermission, setEditPermission] = useState(false);
  const [isSelectMode, setSelectMode] = useState(false);
  const [details, setDetails] = useState({});
  const [newHotspotPosition, setNewHotspotPosition] = useState(null);
  const isSelectModeRef = useRef(isSelectMode);
  const viewerCompRef = useRef(null);
  const showToolbar = editorMode;

  const [showPopup, setShowPopup] = useState({
    chatbot: false,
    aiAnalytics: false,
  });

  // Keep ref in sync with state
  useEffect(() => {
    isSelectModeRef.current = isSelectMode;
  }, [isSelectMode]);

  const onRendererClickEvents = useCallback((click) => {
    if (isSelectModeRef.current) {
      setNewHotspotPosition(click);
      setSelectMode(false);
    }
  }, []); // Empty dependency array since we're using ref

  const createNewHotspot = async (inventory) => {
    try {
      const { x, y, z } = newHotspotPosition.worldCoordinates;
      const { width, height } = await getImageDimensions(inventory.path);
      let bodyData = {
        data: {
          uuid: null,
          name: "Hotspot " + (scene.hotspots.length + 1),
          type: "IMAGE",
          position: {
            x: x,
            y: y,
            z: z,
          },
          scale: {
            x: width * 0.25,
            y: height * 0.25,
            z: 0,
          },
          inventory: inventory,
          timer: {
            start: 0,
          },
          action: [DEFAULT_HOTSPOT_ACTION],
          destination: null,
        },
      };

      let createData = await apiclient.post(`${configData.config.API_URL}${HOTSPOT_CREATE}/${scene.uuid}`, bodyData);

      let _updateSceneList = sceneList.map((preScene) =>
        preScene.uuid === scene.uuid
          ? {
              ...preScene,
              hotspots: [
                ...preScene.hotspots,
                {
                  name: createData.name,
                  type: createData.type,
                  uuid: createData.uuid,
                },
              ],
            }
          : preScene
      );

      setCurrentHotspot(createData);
      setScene((pre) => ({
        ...pre,
        hotspots: [...pre.hotspots, createData],
      }));
      setNewHotspotPosition(null);
      dispatch(saveSceneList({ data: _updateSceneList }));
    } catch (error) {
      console.log(error);
    }
  };

  const generateInventoryThumbnail = async (noThumbnailInventoryIds) => {
    try {
      let bodyData = {
        ids: noThumbnailInventoryIds,
      };
      await apiclient.post(`${configData.config.API_URL}${INVENTORY_THUMBNAIL_GENERATE}`, bodyData);
      getProjectInventoryList(uuid);
    } catch (error) {
      console.log(error);
    }
  };

  const getProjectInventoryList = async (uuid) => {
    try {
      let data = await apiclient.get(`${configData.config.API_URL}${PROJECT_INVENTORY}/${uuid}`);
      let noThumbnailInventoryIds = data.inventoryList
        .filter((e) => ["IMAGE"].includes(e.fileType))
        .filter((f) => !f.thumbnail)
        .map((g) => g.uuid);
      dispatch(saveInventoryList({ data: data.inventoryList }));
      dispatch(saveDefaultInventoryList({ data: data.defaultInventory }));
      // if (noThumbnailInventoryIds.length > 0) {
      //   generateInventoryThumbnail(noThumbnailInventoryIds);
      // }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!currentHotspot?.uuid) return;

    setScene((prevScene) => {
      const updatedHotspots = prevScene.hotspots.map((hotspot) =>
        hotspot?.uuid === currentHotspot?.uuid ? currentHotspot : hotspot
      );

      return {
        ...prevScene,
        hotspots: updatedHotspots,
      };
    });
  }, [currentHotspot]);

  useEffect(() => {
    getProjectInventoryList(uuid);
    return () => {
      dispatch(resetInventoryList());
    };
  }, [uuid]);

  const updateSceneDetails = async () => {
    try {
      let sceneDetails = JSON.parse(JSON.stringify(scene));
      if (!sceneDetails?.inventory?.uuid) {
        sceneDetails = {
          ...sceneDetails,
          inventory: null,
        };
      }

      let bodyData = {
        data: sceneDetails,
      };

      await apiclient.post(`${configData.config.API_URL}${SCENE_DETAILS_UPDATE}/${sceneDetails.uuid}`, bodyData);
      dispatch(triggerFetchScene());
    } catch (error) {
      console.log(error);
    }
  };

  const getProjectDetails = async () => {
    try {
      let data = await apiclient.get(`${configData.config.API_URL}${PROJECT_DETAILS}/${uuid}`);
      const editorModeValue = Boolean(data.accessMode);
      if(!editorModeValue)return navigate(`/view/${uuid}`);
      setDetails(data);
      setEditPermission(isMobileView || !showToolbarProps ? false : editorModeValue);
      setEditorMode(isMobileView || !showToolbarProps ? false : editorModeValue);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape" && isSelectModeRef) {
        setSelectMode(false);
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("keydown", handleEsc);
    };
  }, []);

  useEffect(() => {
    getProjectDetails();
  }, [showToolbarProps, uuid]);

  useDebouncedAsyncEffect(
    async () => {
      if (scene.uuid && updateSceneProps) {
        await updateSceneDetails();
      }
    },
    [scene],
    1000
  );

  // Reset editor-related states when leaving the editor (on unmount)
  useEffect(() => {
    return () => {
      // Reset Redux slices
      dispatch(resetInventoryList());
      dispatch(resetSceneList());

      // Reset local component states to defaults
      setScene(defaultScene);
      setCurrentHotspot({});
      setShowLibrary(false);
      setEditorMode(true);
      setEditPermission(false);
      setSelectMode(false);
      setDetails({});
      setNewHotspotPosition(null);
      setShowPopup({ chatbot: false, aiAnalytics: false });
    };
  }, []);

  const handleBackClick = () => {
    navigate(`/project/${userData.user.uuid}`);
  };

  const fetchSceneDetails = useCallback(
    async (uuid) => {
      if (!uuid || !sceneList.length) return;
      try {
        let data = sceneList?.find((item) => item.uuid === uuid);
        if (!data?.inventory) {
          data = { ...data, inventory: defaultScene.inventory };
        }
        if (viewerCompRef.current && viewerCompRef.current.handleSceneChange) {
          viewerCompRef.current.handleSceneChange(data);
        }
      } catch (error) {
        console.log(error);
      }
    },
    [configData.config.API_URL, sceneList]
  );

  /**
   * Smoothly animate camera to center on a hotspot
   * @param {Object} hotspotPosition - The hotspot position {x, y, z}
   * @param {number} duration - Animation duration in milliseconds (default: 1000)
   * @param {Function} onComplete - Optional callback when animation completes
   */

  const animateToHotspotPosition = useCallback((hotspotPosition, duration = 1000, onComplete) => {
    // Get Three.js refs from global window objects
    const refs = {
      cameraRef: { current: window._threeDViewerCamera },
      controlsRef: { current: window._threeDViewerControls },
    };

    if (!refs.cameraRef.current || !refs.controlsRef.current) {
      console.warn("animateToHotspotPosition: Camera or controls not available");
      return;
    }

    // Use the utility function from threeUtils
    return animateToHotspot(hotspotPosition, refs, duration, onComplete);
  }, []);

  if (!details.uuid) return <FullScreenLoader loading={true} />;

  return (
    <>
      <div className={`editorPage ${!editorMode ? "hideToolbar" : ""}`}>
        {editPermission && (
          <div className="editorHeader-parent">
            <Header
              editorMode={editorMode}
              setEditorMode={setEditorMode}
              projectId={uuid}
              details={details}
              setDetails={setDetails}
              getProjectDetails={getProjectDetails}
            />
          </div>
        )}

        <div className="editorMain-parent">
          <div className="ThreeDViewer">
            <ViewerComp
              ref={viewerCompRef}
              editorMode={editorMode}
              editPermission={editPermission}
              scene={scene}
              setScene={setScene}
              currentHotspot={currentHotspot}
              setCurrentHotspot={setCurrentHotspot}
              isSelectMode={isSelectMode}
              onRendererClickEvents={onRendererClickEvents}
              fetchSceneDetails={fetchSceneDetails}
              handleBackClick={handleBackClick}
            />
          </div>

          {!editPermission && showBackButton && (
            <div className="viewerBack" onClick={handleBackClick}>
              <BackButton Icon={<BackArrowIcon />} />
            </div>
          )}

          <Toolbar
            showLibrary={showLibrary}
            setShowLibrary={setShowLibrary}
            projectId={uuid}
            visible={showToolbar}
            scene={scene}
            setScene={setScene}
            currentHotspot={currentHotspot}
            setCurrentHotspot={setCurrentHotspot}
            fetchSceneDetails={fetchSceneDetails}
            createNewHotspot={createNewHotspot}
            setSelectMode={setSelectMode}
            animateToHotspotPosition={animateToHotspotPosition}
          />
        </div>
      </div>

      <LibraryPopup show={showLibrary} handleClose={() => setShowLibrary(false)} />
      <IconsPopup
        hotspotId={null}
        show={newHotspotPosition !== null}
        handleClose={() => setNewHotspotPosition(null)}
        handleChangeHotspot={createNewHotspot}
      />
     {
       editorMode && 
       <ChatBot showPopup={showPopup} setShowPopup={setShowPopup} />
      }
    </>
  );
};

export default EditorMain;
